/** Row label for training video lists and dialogs: description if set, else stored filename / URL leaf. */
export function getTrainingVideoListLabel(video: {
  description: string | null;
  name: string | null;
  url: string;
}): string {
  const desc = video.description?.trim();
  if (desc) return desc;
  return video.name ?? video.url.split("/").pop() ?? "Video";
}

/** Human-friendly order for titles with embedded numbers (e.g. "… #2" before "… #10"). */
const TRAINING_VIDEO_LABEL_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export function compareTrainingVideoLabels(
  a: { description: string | null; name: string | null; url: string },
  b: { description: string | null; name: string | null; url: string },
): number {
  return TRAINING_VIDEO_LABEL_COLLATOR.compare(
    getTrainingVideoListLabel(a),
    getTrainingVideoListLabel(b),
  );
}

/** Canonical order and labels — mirrors Curriculum → Select Subject (pre-visibility filter). */
export const TRAINING_VIDEO_CURRICULUM_TAB_ORDER = [
  "mooshim",
  "innae",
  "moogongryu",
  "taegeuk",
  "kukkiwon-wt",
  "songmookwan",
  "choongmoo",
  "one-step",
  "ho-shin-sul",
  "geukki",
  "sparring",
  "non-combat",
] as const;

export type TrainingVideoCurriculumTabValue =
  (typeof TRAINING_VIDEO_CURRICULUM_TAB_ORDER)[number];

export const TRAINING_VIDEO_CURRICULUM_TAB_LABELS: Record<
  TrainingVideoCurriculumTabValue,
  { label: string; mobileLabel: string }
> = {
  mooshim: { label: "Mooshim", mobileLabel: "Mooshim" },
  innae: { label: "Innae", mobileLabel: "Innae" },
  moogongryu: { label: "Moo Gong Ryu", mobileLabel: "Moo Gong Ryu" },
  taegeuk: { label: "Taegeuk", mobileLabel: "Taegeuk" },
  "kukkiwon-wt": { label: "Kukkiwon / WT", mobileLabel: "Kukkiwon / WT" },
  songmookwan: { label: "Song Moo Kwan", mobileLabel: "Song Moo Kwan" },
  choongmoo: { label: "Choong Moo", mobileLabel: "Choong Moo" },
  "one-step": { label: "One-Step", mobileLabel: "One-Step" },
  "ho-shin-sul": { label: "Ho Shin Sul", mobileLabel: "Ho Shin Sul" },
  geukki: { label: "Geukki", mobileLabel: "Geukki" },
  sparring: { label: "Sparring/Breaking", mobileLabel: "Spar/Break" },
  "non-combat": { label: "Theory/Other", mobileLabel: "Theory/Other" },
};

export const TRAINING_VIDEO_UNCATEGORIZED_TAB_KEY = "__uncategorized__";

export type TrainingVideoTabBucket = {
  tabKey: string;
  tabLabel: string;
  tabMobileLabel: string;
  sortIndex: number;
};

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/_/g, "-");
}

const compact = (s: string) => s.replace(/-/g, "");

/** Map Training sub-album name/string to a curriculum tab value when possible. */
export function matchAlbumToCurriculumTab(
  albumName: string,
  albumString: string,
): TrainingVideoCurriculumTabValue | null {
  const candidates = [slugify(albumString), slugify(albumName)];

  const synonyms: Record<string, TrainingVideoCurriculumTabValue> = {
    taeguek: "taegeuk",
    taeguk: "taegeuk",
    "moo-gong-ryu": "moogongryu",
    moogongryu: "moogongryu",
    "song-moo-kwan": "songmookwan",
    songmookwan: "songmookwan",
    "choong-moo": "choongmoo",
    choongmoo: "choongmoo",
    "ho-shin-sul": "ho-shin-sul",
    hoshinsul: "ho-shin-sul",
    "one-step": "one-step",
    onestep: "one-step",
    geukki: "geukki",
    sparring: "sparring",
    "non-combat": "non-combat",
    theory: "non-combat",
    "theory-other": "non-combat",
    kukkiwon: "kukkiwon-wt",
    wt: "kukkiwon-wt",
    "kukkiwon-wt": "kukkiwon-wt",
  };

  for (const raw of candidates) {
    const syn = synonyms[raw] ?? synonyms[compact(raw)];
    if (syn) return syn;
  }

  for (const tab of TRAINING_VIDEO_CURRICULUM_TAB_ORDER) {
    const tabC = compact(tab);
    for (const c of candidates) {
      if (c === tab || compact(c) === tabC) return tab;
    }
  }

  return null;
}

export function uncategorizedTrainingVideoTabBucket(): TrainingVideoTabBucket {
  return {
    tabKey: TRAINING_VIDEO_UNCATEGORIZED_TAB_KEY,
    tabLabel: "Other",
    tabMobileLabel: "Other",
    sortIndex: 9999,
  };
}

export function subfolderAlbumToTabBucket(sub: {
  album_name: string;
  album_string: string;
}): TrainingVideoTabBucket {
  const curriculum = matchAlbumToCurriculumTab(sub.album_name, sub.album_string);
  if (curriculum) {
    const idx = TRAINING_VIDEO_CURRICULUM_TAB_ORDER.indexOf(curriculum);
    const L = TRAINING_VIDEO_CURRICULUM_TAB_LABELS[curriculum];
    return {
      tabKey: curriculum,
      tabLabel: L.label,
      tabMobileLabel: L.mobileLabel,
      sortIndex: idx,
    };
  }
  const key = `custom:${sub.album_string}`;
  const label = sub.album_name;
  return {
    tabKey: key,
    tabLabel: label,
    tabMobileLabel: label.length > 18 ? `${label.slice(0, 16)}…` : label,
    sortIndex: 1000,
  };
}
